@file:OptIn(ExperimentalBlockRichTextApi::class)

package com.taocent.simple.compose.component.blockrichtext

import androidx.compose.ui.text.TextRange
import androidx.compose.ui.text.input.TextFieldValue
import com.taocent.simple.compose.component.blockrichtext.internal.block.EditorCursor
import com.taocent.simple.compose.component.blockrichtext.internal.undo.BlockUndoManager

/**
 * BlockState 文档级 undo/redo 与文本块内容同步相关的扩展函数。
 *
 * - [updateBlockContent] 把 RichTextState 状态同步到 DocumentBlock
 * - [recordChange] 记录单块文本变更
 * - [documentSnapshot] / [recordDocumentChange] / [restoreDocumentSnapshot] 文档级快照
 * - [withContentUndoSuppressed] 在批量操作中抑制单块 undo 记录
 */

fun BlockState.updateBlockContent(blockId: String) {
    val state = blockStates[blockId] ?: return
    val index = findBlockIndex(blockId)
    if (index >= 0) {
        val block = _blocks[index]
        if (block is TextBlock) {
            val oldValue = TextFieldValue(
                annotatedString = block.content,
                selection = state.textFieldValue.selection
            )
            val newValue = state.textFieldValue
            _blocks[index] = block.copy(
                content = newValue.annotatedString
            )
            if (!suppressContentUndoRecord && oldValue.annotatedString != newValue.annotatedString) {
                blockUndoManager.record(blockId, oldValue, newValue)
            }
        }
    }
}

/**
 * 将 [blockStates] 中的当前内容同步到 [TextBlock.content] 缓存字段，
 * **不**记录 undo。
 *
 * 用于导航逻辑等已自行调用 [recordDocumentChange] 记录文档级快照的场景，
 * 避免与 BlockUndoManager 的单块记录重复/冲突。
 */
fun BlockState.syncBlockContent(blockId: String) {
    val state = blockStates[blockId] ?: return
    val index = findBlockIndex(blockId)
    if (index >= 0) {
        val block = _blocks[index]
        if (block is TextBlock) {
            _blocks[index] = block.copy(content = state.textFieldValue.annotatedString)
        }
    }
}

fun BlockState.recordChange(blockId: String, before: TextFieldValue, after: TextFieldValue) {
    blockUndoManager.record(blockId, before, after)
}

internal fun BlockState.documentSnapshot(): BlockUndoManager.DocumentSnapshot {
    return BlockUndoManager.DocumentSnapshot(
        blocks = _blocks.toList(),
        textValues = blockStates.mapValues { it.value.textFieldValue },
        cursor = editorCursor,
        focusedBlockId = focusedBlockId,
        selectedTableBlockId = selectedTableBlockId,
        navSelectedTableId = navSelectedTableId,
        navSourceBlockId = navSourceBlockId
    )
}

internal fun BlockState.recordDocumentChange(
    before: BlockUndoManager.DocumentSnapshot,
    mergeKey: String? = null
) {
    blockUndoManager.recordDocument(before, documentSnapshot(), mergeKey)
    pendingHyperlinkDeleteSnapshot = null
    pendingHyperlinkDeleteKey = null
}

internal inline fun BlockState.withContentUndoSuppressed(block: () -> Unit) {
    val previous = suppressContentUndoRecord
    suppressContentUndoRecord = true
    try {
        block()
    } finally {
        suppressContentUndoRecord = previous
    }
}

internal fun BlockState.restoreDocumentSnapshot(snapshot: BlockUndoManager.DocumentSnapshot) = withContentUndoSuppressed {
    _blocks.clear()
    blockStates.clear()
    snapshot.blocks.forEach { block ->
        if (block is TextBlock) {
            val value = snapshot.textValues[block.id] ?: TextFieldValue(
                annotatedString = block.content,
                selection = TextRange(block.content.length)
            )
            _blocks.add(block)
            val state = createBlockState()
            state.restoreTextFieldValue(value)
            blockStates[block.id] = state
            syncBlockContent(block.id)
        } else {
            _blocks.add(block)
        }
    }
    rebuildIdToIndex()
    focusedBlockId = snapshot.focusedBlockId
    editorCursor = snapshot.cursor
    selectedTableBlockId = snapshot.selectedTableBlockId
    navSelectedTableId = snapshot.navSelectedTableId
    navSourceBlockId = snapshot.navSourceBlockId
    clearTableCellFocus()
    contentSyncVersion++
    if (snapshot.cursor is EditorCursor.Text && _blocks.any { it.id == snapshot.focusedBlockId && it is TextBlock }) {
        requestFocusedBlockFocus()
    }
}
